/**
 * Force-dark (Samsung Internet / Chrome) inverts CSS background-clip gradients.
 * Rasterize titles to PNG <img> so colors stay (images are only dimmed, not inverted)
 * while still supporting multi-line wrap.
 */
(() => {
  if (window.__rokidGradientTitleInit) return;
  window.__rokidGradientTitleInit = true;

  const SELECTOR = '[data-rokid-gradient-title]:not([data-rokid-gradient-done])';
  const STOPS = [
    [0, '#fddef4'],
    [0.4952, '#a5efb3'],
    [1, '#7ad0f8'],
  ];

  const wrapLines = (text, maxWidth, measure) => {
    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) return [text];

    const lines = [];
    let current = '';

    const pushCharWrapped = (token) => {
      let chunk = '';
      for (const char of token) {
        const test = chunk + char;
        if (chunk && measure(test) > maxWidth) {
          lines.push(chunk);
          chunk = char;
        } else {
          chunk = test;
        }
      }
      current = chunk;
    };

    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (current && measure(test) > maxWidth) {
        lines.push(current);
        current = '';
        if (measure(word) > maxWidth) {
          pushCharWrapped(word);
        } else {
          current = word;
        }
      } else if (!current && measure(word) > maxWidth) {
        pushCharWrapped(word);
      } else {
        current = test;
      }
    }

    if (current) lines.push(current);
    return lines.length ? lines : [text];
  };

  const measureTitleWidth = (el) => {
    const parentW = el.parentElement?.clientWidth || el.clientWidth || 0;
    const cs = getComputedStyle(el);
    let width = parentW || el.clientWidth || 0;
    const maxW = cs.maxWidth;
    if (maxW && maxW !== 'none') {
      const parsed = parseFloat(maxW);
      if (!Number.isNaN(parsed) && parsed > 0) width = Math.min(width || parsed, parsed);
    }
    return Math.floor(Math.max(width, 1));
  };

  const buildImage = (sourceEl, text, widthHint) => {
    const cs = getComputedStyle(sourceEl);
    const fontSize = parseFloat(cs.fontSize) || 16;
    const fontWeight = cs.fontWeight || '600';
    const fontFamily = cs.fontFamily || 'sans-serif';
    const maxWidth = Math.floor(widthHint || measureTitleWidth(sourceEl));
    if (maxWidth < 8) return null;

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) return null;
    measureCtx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

    const lines = wrapLines(text, maxWidth, (value) => measureCtx.measureText(value).width);

    // Use real font metrics so descenders (g/y/p) and ascenders are not clipped
    const metrics = measureCtx.measureText(lines.join('AgypQ'));
    const ascent =
      metrics.actualBoundingBoxAscent || metrics.fontBoundingBoxAscent || fontSize * 0.8;
    const descent =
      metrics.actualBoundingBoxDescent || metrics.fontBoundingBoxDescent || fontSize * 0.25;
    const padY = Math.ceil(fontSize * 0.08);
    const lineHeight = Math.ceil(ascent + descent + padY);
    const height = Math.ceil(Math.max(lines.length, 1) * lineHeight + padY);
    const dpr = Math.min(window.devicePixelRatio || 1, 3);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(maxWidth * dpr));
    canvas.height = Math.max(1, Math.ceil(height * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.scale(dpr, dpr);
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';

    const gradient = ctx.createLinearGradient(0, 0, maxWidth, maxWidth * 0.175);
    STOPS.forEach(([offset, color]) => gradient.addColorStop(offset, color));
    ctx.fillStyle = gradient;

    lines.forEach((line, index) => {
      const baseline = padY + ascent + index * lineHeight;
      ctx.fillText(line, 0, baseline);
    });

    const img = document.createElement('img');
    img.className = `${sourceEl.className} rokid-card-title--img`.trim();
    img.alt = text;
    img.width = maxWidth;
    img.height = height;
    img.decoding = 'async';
    img.setAttribute('data-rokid-gradient-done', '');
    img.dataset.rokidGradientTitleText = text;
    img.dataset.rokidGradientWidth = String(maxWidth);
    img.style.display = 'block';
    img.style.width = `${maxWidth}px`;
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.opacity = '1';
    img.style.background = 'none';
    img.style.forcedColorAdjust = 'none';

    const maxWVar =
      sourceEl.style.getPropertyValue('--title-max-w') || cs.getPropertyValue('--title-max-w');
    if (maxWVar && maxWVar.trim() && maxWVar.trim() !== 'none') {
      img.style.maxWidth = maxWVar.trim();
    }

    try {
      img.src = canvas.toDataURL('image/png');
    } catch (_) {
      return null;
    }

    return img;
  };

  const observeResize = (img, sampleEl) => {
    const parent = img.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') return;

    let frame = 0;
    let lastParentWidth = parent.clientWidth;

    const ro = new ResizeObserver(() => {
      const currentParentWidth = parent.clientWidth;

      // 父容器宽度没有变化时，无需重新计算标题
      if (currentParentWidth === lastParentWidth) return;

      lastParentWidth = currentParentWidth;
      cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        const probe = sampleEl.cloneNode(false);
        probe.className = img.className.replace(/\brokid-card-title--img\b/g, '').trim();
        probe.textContent = img.dataset.rokidGradientTitleText || '';
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        probe.style.pointerEvents = 'none';
        probe.style.left = '0';
        probe.style.top = '0';
        parent.appendChild(probe);

        const nextWidth = measureTitleWidth(probe);
        const prevWidth = Number(img.dataset.rokidGradientWidth || 0);
        const text = img.dataset.rokidGradientTitleText || '';
        probe.remove();

        if (Math.abs(nextWidth - prevWidth) < 2) return;

        const nextProbe = sampleEl.cloneNode(false);
        nextProbe.className = img.className.replace(/\brokid-card-title--img\b/g, '').trim();
        nextProbe.textContent = text;
        parent.appendChild(nextProbe);
        const nextImg = buildImage(nextProbe, text, nextWidth);
        nextProbe.remove();
        if (!nextImg) return;

        ro.disconnect();
        img.replaceWith(nextImg);
        observeResize(nextImg, sampleEl);
      });
    });

    ro.observe(parent);
  };

  const enhance = (el) => {
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return;

    el.setAttribute('data-rokid-gradient-done', '');
    const sample = el.cloneNode(false);
    const img = buildImage(el, text);
    if (!img) {
      el.removeAttribute('data-rokid-gradient-done');
      // Readable solid fallback if canvas fails under force-dark
      el.style.setProperty('background-image', 'none', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.opacity = '1';
      return;
    }

    el.replaceWith(img);
    observeResize(img, sample);
  };

  const run = async () => {
    try {
      if (document.fonts?.ready) await document.fonts.ready;
    } catch (_) {
      /* ignore */
    }
    document.querySelectorAll(SELECTOR).forEach(enhance);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
})();
